import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bookmark, BookmarkPlus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SavedViews({ scope = "albaranes", currentFilters, pageSize, onApply }) {
  const [views, setViews] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/api/v1/views", { params: { scope } });
      setViews(data.items);
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => { load(); }, [scope]); // eslint-disable-line

  const save = async () => {
    if (!name.trim()) return toast.error("Nombre requerido");
    setLoading(true);
    try {
      await api.post("/api/v1/views", { name: name.trim(), scope, filters: currentFilters || {}, page_size: pageSize });
      setName("");
      toast.success(`Vista "${name.trim()}" guardada`);
      load();
    } catch (e) {
      toast.error("No se pudo guardar la vista");
    } finally { setLoading(false); }
  };

  const removeView = async (id) => {
    try {
      await api.delete(`/api/v1/views/${id}`);
      toast.info("Vista eliminada");
      load();
    } catch (_e) {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="btn-saved-views">
          <Bookmark className="h-4 w-4 mr-2"/> Vistas
          {views.length > 0 ? <span className="ml-2 text-[10px] bg-slate-200 rounded px-1.5 py-0.5 font-mono">{views.length}</span> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="views-popover">
        <div className="p-3 border-b border-slate-200">
          <div className="label-tag mb-1">Guardar vista actual</div>
          <div className="flex gap-2">
            <Input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Ej. Ventas Serie A mes actual" data-testid="view-name-input" className="h-9"/>
            <Button size="sm" onClick={save} disabled={loading} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="btn-save-view">
              {loading ? <Loader2 className="h-4 w-4 animate-spin"/> : <BookmarkPlus className="h-4 w-4"/>}
            </Button>
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {views.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center">Sin vistas guardadas aún.</div>
          ) : views.map((v) => (
            <div key={v.id} className="px-3 py-2 border-b border-slate-100 last:border-b-0 flex items-start gap-2 hover:bg-slate-50" data-testid={`view-${v.id}`}>
              <button
                type="button"
                className="flex-1 text-left min-w-0"
                onClick={() => onApply?.(v)}
                data-testid={`btn-apply-view-${v.id}`}
              >
                <div className="text-sm font-semibold text-slate-900 truncate">{v.name}</div>
                <div className="text-[11px] text-slate-500 font-mono truncate">
                  {Object.entries(v.filters || {}).filter(([, val]) => val).map(([k, val]) => `${k}=${val}`).join(" · ") || "sin filtros"}
                </div>
              </button>
              <button type="button" onClick={() => removeView(v.id)} className="text-slate-400 hover:text-red-600 p-1" aria-label="Eliminar" data-testid={`btn-del-view-${v.id}`}>
                <Trash2 className="h-3.5 w-3.5"/>
              </button>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
